<template>
  <div class="workflow workflow-font-family">
    <div class="workflow-loader" v-if="loading && !shouldRender">
      <div class="progress-icon"></div>
    </div>
    <template v-if="!loading && shouldRender">
      <WorkFlowHeader
        :process="process"
        :isUserHasAccess="isUserHasAccess"
        :isTableIdArray="isTableIdArray"
        :flowRunnerEndpoint="flowRunnerEndpoint"
        :hasUnsavedChanges="hasUnsavedChanges"
        :socketData="socketData"
        @showToaster="showToaster"
        @setRunFlowError="setRunFlowError"
        @setHtmlViewer="setHtmlViewer"
        @setProcess="setProcess"
        @setReferenceProcess="setReferenceProcess"
        @saveZoom="saveZoomToLocalStorage"
        @onSocketDisconnect="onSocketDisconnect"
      />

      <div class="workflow-body" v-show="!isFlowEmpty">
        <WorkFlowBuilder
          ref="WorkFlowBuilderRef"
          :process="process"
          :tasksArray="getTasksArray"
          :tasksMiscData="getTasksMiscData"
          :isUserHasAccess="isUserHasAccess"
          :flowRunnerEndpoint="flowRunnerEndpoint"
          :isTableIdArray="isTableIdArray"
          :databases="databases"
          :getDatabaseSchema="getDatabaseSchema"
          :customTasksList="getWorkflowCustomTasks"
          :runFlowError="runFlowError"
          :isAdmin="isAdmin"
          :isFloatingTaskBar="isFloatingTaskBar"
          @fetchAndAddSpecificCustomTask="fetchAndAddSpecificCustomTask"
          @showToaster="showToaster"
          @setRunFlowError="setRunFlowError"
          @setProcess="setProcess"
          @onTaskAdd="onTaskAdd"
          @setTaskbarMode="setTaskbarMode"
        />
        <div class="workflow-taskbar-wrapper" v-show="!isFloatingTaskBar">
          <WorkFlowTaskBar
            ref="WorkFlowTaskBarRef"
            :process="process"
            :tasksArray="getTasksArray"
            :tasksMiscData="getTasksMiscData"
            :isUserHasAccess="isUserHasAccess"
            :customTasksList="getWorkflowCustomTasks"
            @onTaskAdd="onTaskAdd"
            @onClose="onCloseTaskBar"
            @setTaskbarMode="setTaskbarMode"
          />
        </div>
      </div>
      <div v-if="isFlowEmpty" class="empty-workflow-container">
        <div class="init-task-add-button" @click="initiateWorkFlow">
          <svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M1.92871 10H18.9998M10.4642 18.5355V1.46448"
              stroke="white"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </div>
      </div>

      <HtmlViewer
        v-if="htmlViewer.isHtmlViewerVisible"
        :myHtml="htmlViewer.html"
        :myJson="htmlViewer.json"
        @close="hideHtmlViewer()"
      />
    </template>
  </div>
</template>
<script>
import Vue from 'vue';
import WorkFlowHeader from '../components/WorkFlow/WorkFlowHeader.vue';
import WorkFlowTaskBar from '../components/WorkFlow/WorkFlowTaskBar.vue';
import WorkFlowBuilder from '../components/WorkFlow/WorkFlowBuilder.vue';
import HtmlViewer from '../components/modals/htmlViewer.vue';
import {
  workFlowTasks,
  workFlowTasksMiscData,
  workFlowAccessLevel,
  workflowCustomTasksList,
} from '../utils/constants/WorkFlowConstants';
import {
  generateTaskObject,
  generateTaskMiskDataObject,
  getValidatedTasksPosition,
} from '../utils/helpers/WorkFlowHelpers';

import {
  getWorkFlowById,
  getSharedWorkFlowUsers,
  getDataBaseTableSchema,
  getCustomDatabaseItem,
  getCustomDatabaseItems,
  getUserDataByID,
} from '../services/axios/data.api';
import { mapGetters } from 'vuex';
import io from 'socket.io-client';

export default {
  name: 'WorkFlow',
  data() {
    return {
      loading: false,
      process: null,
      referenceProcess: null,
      flowPermission: {},
      runFlowError: null,
      htmlViewer: {
        isHtmlViewerVisible: false,
        html: null,
        json: null,
      },
      customTasks: [],
      customMiscData: {},
      unsavedDataHandler: null,
      shouldRender: false,
      taskbarMode: 'floating', // 'floating' || 'static'
      socketData: {
        flow: {},
        users: [],
        usersData: {},
      },
    };
  },
  props: {
    processID: {
      type: String | undefined,
      required: true,
    },
    databases: {
      type: Array,
      required: true,
    },
  },
  methods: {
    checkTasksPostion() {
      if (this.process && this.process.tasks && this.process.tasks.length) {
        const data = [...this.process.tasks].map((el) => {
          const { x, y } = getValidatedTasksPosition(
            el.shape.x,
            el.shape.y,
            el.name
          );
          return { ...el, shape: { ...el.shape, x, y } };
        });

        this.process.tasks = [...data];
        this.referenceProcess = JSON.stringify(this.process);
      }
    },
    onTaskAdd(task) {
      const builderRef = this.$refs.WorkFlowBuilderRef;
      if (builderRef) {
        builderRef?.setActiveTask(task);
      }
    },
    saveZoomToLocalStorage() {
      const builderRef = this.$refs.WorkFlowBuilderRef;
      if (builderRef) {
        localStorage.setItem(
          `workFlowZoom-${this.user.username}`,
          builderRef?.zoom?.scale ? builderRef.zoom.scale : 1
        );
      }
    },
    async checkEditPermission(workFlowId) {
      try {
        const response = await getSharedWorkFlowUsers(workFlowId);
        if (response && response.status === 200) {
          this.flowPermission = response.data.find(
            (user) => user.identity == this.user.username
          );
          if (
            response.data.filter(
              (e) =>
                e.identity == this.user.username && e.access_level != 'execute'
            ).length > 0
          )
            return true;
          else {
            // If user has no access, redirect to process-list
            this.$router.push({
              path: 'process-list',
            });
          }
        }
      } catch (e) {
        this.$router.push({
          path: 'process-list',
        });
        throw new Error(e);
      }
    },
    async fetchData() {
      try {
        const response = await getWorkFlowById(this.processID);
        if (response && response.status === 200) {
          const { data } = response;
          if (!!data.version && data.version >= 3) {
            return this.$router.push({
              path: '/',
              query: {
                section: 'flows',
              },
            });
          }
          if (!data.formTrigger) data.formTrigger = null;

          if (!data.settings.hasOwnProperty('btnCaption'))
            data.settings.btnCaption = 'Starten';
          if (!data.settings.hasOwnProperty('loadingCaption'))
            data.settings.loadingCaption = 'Ladevorgang läuft...';
          if (!data.settings.hasOwnProperty('fullWidth'))
            data.settings.fullWidth = false;
          if (!data.settings.hasOwnProperty('brandingColor'))
            data.settings.brandingColor = '#000';
          if (
            !data.settings.hasOwnProperty('isHtmlElementVisible') &&
            this.isAdmin
          )
            data.settings.isHtmlElementVisible = true;
          if (!data.settings.hasOwnProperty('workflowIcon')) {
            data.settings.workflowIcon = {
              icon: null,
              linkType: null,
            };
          }
          if (data.hasOwnProperty('tableID') && Array.isArray(data.tableID)) {
            data.tableID = data.tableID.filter(
              (databaseName) => databaseName.trim() != ''
            );
          }

          if (
            data?.tasks?.length >= 0 &&
            data?.tasks?.some((el) => el.name == 'CustomCode')
          ) {
            this.generateCustomTaskMiscObject('CustomCode');
          }
          this.process = data;
          this.referenceProcess = JSON.stringify(data);
          this.checkTasksPostion();

          if (this.process.owner != this.user.username)
            await this.checkEditPermission(this.processID);
        }
      } catch (error) {
        if (error.status === 400 && error.data?.message === 'Access denied.') {
          await this.$router.push({
            path: '/',
            query: {
              section: 'flows',
            },
          });
        }
        throw error;
      }
    },
    addMissingAttributesToFlow() {
      const startTaskIndex = this.process.tasks.findIndex(
        (el) => el.name === 'Start'
      );

      if (startTaskIndex > -1) {
        // Add structure to each element
        for (let el of this.process.tasks[startTaskIndex].props.formTrigger
          .elements) {
          if (!el.hasOwnProperty('structure')) {
            el.structure = { tab: 0, row: 0, col: 0 };
          }
        }

        // Add structure to task
        if (
          !this.process.tasks[startTaskIndex].props.formTrigger.hasOwnProperty(
            'structure'
          )
        ) {
          this.process.tasks[startTaskIndex].props.formTrigger.structure = {
            tabs: [
              {
                id: 0,
                rows: [
                  {
                    id: 0,
                    cols: [
                      {
                        id: 0,
                      },
                    ],
                  },
                ],
              },
            ],
          };
        }
      }
    },
    setRunFlowError(taskID) {
      this.runFlowError = taskID;
    },
    hideHtmlViewer() {
      this.htmlViewer = {
        isHtmlViewerVisible: false,
        html: null,
        json: null,
      };
    },
    setHtmlViewer(key, data) {
      this.htmlViewer[key] = data;
    },
    getDatabaseTask() {
      return this.process.tasks.find((task) => task.name === 'Database');
    },
    setLoader(bool) {
      this.loading = bool;
    },
    setProcess(process) {
      this.process = process;
    },
    setReferenceProcess() {
      this.referenceProcess = JSON.stringify(this.flowWithoutInputOutput);
    },
    showToaster(message, title, variant) {
      this.$bvToast.toast(message, {
        title,
        variant,
        solid: true,
      });
    },
    async getDatabaseSchema(tableName) {
      try {
        const response = await getDataBaseTableSchema(tableName);

        if (response && response.status === 200 && response.data) {
          this.$store.commit('SET_DATABASE_SCHEMA', response.data);
          return response.data.data;
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.log({ error });
        return null;
      }
    },
    async fetchAndAddSpecificCustomTask(id) {
      try {
        const response = await getCustomDatabaseItem(
          this.getCustomTaskDatabase[0].name,
          id
        );
        const customIcon =
          'https://res.cloudinary.com/gharnati-com/image/upload/v1681824477/noun-electricity-4806795_shvk5m.png';
        this.customTasks.push(
          generateTaskObject({
            name: 'CustomCode',
            customIcon: response.data.icon ? response.data.icon : customIcon,
            props: {
              ...response.data.code,
              description: response.data.description,
              name: response.data.taskname,
              inputs: response.data.inputfields,
            },
            id: 1,
            caption: response.data.taskname,
          })
        );
        this.generateCustomTaskMiscObject('CustomCode');
      } catch (error) {
        console.log('error :>> ', error);
      }
    },
    async addCustomTaskProcess() {
      if (this.getCustomTaskDatabase.length) {
        const databaseItems = await this.fetchAllCustomTask(
          this.getCustomTaskDatabase[0].name
        );

        if (databaseItems && databaseItems.length) {
          const customIcon =
            'https://res.cloudinary.com/gharnati-com/image/upload/v1681824477/noun-electricity-4806795_shvk5m.png';
          databaseItems.forEach((data, index) => {
            this.customTasks.push(
              generateTaskObject({
                name: 'CustomCode',
                customIcon: data.icon ? data.icon : customIcon,
                props: {
                  ...data.code,
                  description: data.description,
                  name: data.taskname,
                  inputs: data.inputfields,
                },
                id: index,
                caption: data.taskname,
              })
            );
          });
          this.generateCustomTaskMiscObject('CustomCode');
        }
      }
    },
    async fetchAllCustomTask(databaseName) {
      try {
        const response = await getCustomDatabaseItems(
          databaseName,
          '?keys=taskname,icon,description,code,inputfields'
        );
        if (
          response.data &&
          response.data.items &&
          response.data.items.length > 0
        ) {
          return response.data.items.map((database) => {
            return { name: databaseName, ...database };
          });
        }
        return [];
      } catch (error) {
        console.log(error);
      }
    },
    generateCustomTaskMiscObject(taskName) {
      if (taskName == 'CustomCode') {
        this.$set(
          this.customMiscData,
          taskName,
          generateTaskMiskDataObject({
            isDroppable: true,
            isDeletable: true,
            isActive: true,
            icon: '',
            appearance: 'square',
            description: "this is testing description , we'll add more later",
            dropzones: ['before', 'after'],
            options: ['left', 'right'],
          })
        );
      }
    },
    setTaskbarMode(mode) {
      this.taskbarMode = mode;
    },
    onCloseTaskBar() {
      this.taskbarMode = 'floating';
    },
    initiateWorkFlow() {
      const startTask = workFlowTasks.find((el) => el.id === 1);
      if (startTask) {
        this.$refs.WorkFlowTaskBarRef.addTask(startTask, false);
      }
    },
    connectSocket() {
      try {
        const socket = io(process.env.VUE_APP_SOCKET_API, {
          auth: {
            type: 'workflow_users',
          },
        });
        const query = this.$route?.query;

        if (
          socket &&
          query &&
          query?.section &&
          query?.section !== 'process-designer'
        ) {
          return socket?.disconnect();
        }

        if (socket) {
          Vue.prototype.$socketInstance = socket;

          socket.on('connected', () => {
            socket.emit('workflow_user_join', {
              workflowId: this.processID,
              userId: this.user.username,
            });

            socket.on(
              `workflow_user_update_${this.processID}`,
              async (userJoinedData) => {
                if (userJoinedData) {
                  const data = JSON.parse(userJoinedData);
                  const { usersList, ...rest } = data;

                  await Promise.all(
                    usersList.map(async (el) => {
                      if (!this.socketData.usersData[el.id]) {
                        const res = await getUserDataByID(el.id, 'name');
                        if (res && res.status == 200 && res.data) {
                          this.socketData.usersData[el.id] = res.data;
                        }
                      }
                    })
                  );

                  this.socketData.flow = rest;
                  this.socketData.users = usersList;
                }
              }
            );

            socket.on(
              'workflow_user_active_check_' +
                this.processID +
                this.user.username,
              async (sessionTimeStamp) => {
                if (sessionTimeStamp != sessionStorage.getItem('sessionId')) {
                  socket.emit('workflow_user_join', {
                    workflowId: this.processID,
                    userId: this.user.username,
                  });
                }
              }
            );
          });
        }
      } catch (e) {
        console.log('socket connect error :', e);
      }
    },
    onSocketDisconnect(processId = null) {
      this.$socketInstance?.emit('workflow_user_leave', {
        workflowId:
          processId && typeof processId === 'string'
            ? processId
            : this.processID,
        userId: this.user.username,
        userLeaveTime: sessionStorage.getItem('sessionId') || '',
      });
      this.$socketInstance?.disconnect();
      delete Vue.prototype.$socketInstance;
      window.removeEventListener('unload', this.onSocketDisconnect);
      window.addEventListener('popstate', this.onSocketDisconnect);
    },
    showAccessDeniedToast() {
      this.showToaster(
        'Zugriff abgelehnt. Sie haben keine Berechtigung, auf diesen Flow zuzugreifen.',
        'Fehler',
        'danger'
      );
    },
    async getDatabaseTaskData() {
      if (this.process && this.process.tasks) {
        const databaseTask = this.getDatabaseTask();
        if (databaseTask && databaseTask?.props?.database) {
          try {
            const databaseSchema = JSON.parse(
              await this.getDatabaseSchema(databaseTask.props.database)
            );
            if (!databaseSchema) {
              throw new Error('unable to get database schema');
            }
            for (const key in databaseTask.props.item) {
              if (!databaseSchema.some((item) => item.name === key)) {
                delete databaseTask.props.item[key];
              }
            }
            if (
              databaseSchema?.length >
              Object.keys(databaseTask.props.item)?.length
            ) {
              this.showToaster(
                `Das Datenbankschema hat sich seit der letzten Anpassung verändert. Bitte öffnen Sie den Task ${databaseTask.caption} für mehr informationen`,
                'Hinweis',
                'warning'
              );
            }
          } catch (error) {
            this.showToaster(
              `Beim überprüfen des Datenbankschemas für den Task ${
                this.getDatabaseTask()?.caption
              } ist ein Fehler aufgetreten. Bitte prüfen Sie, ob die Datenbank vorhanden ist oder melden sich bei Ihrem Administrator.`,
              'Fehler',
              'danger'
            );
          }
        }
      }
    },

    async loadWorkflowData() {
      try {
        await this.fetchData();
        await this.addCustomTaskProcess();
        await this.getDatabaseTaskData();
        setTimeout(async () => {
          await this.addMissingAttributesToFlow();
        });
      } catch (error) {
        throw error;
      }
    },
  },
  async created() {
    try {
      this.setLoader(true);
      this.shouldRender = false;
      window.addEventListener(
        'beforeunload',
        (this.unsavedDataHandler = (event) => {
          if (this.hasUnsavedChanges) {
            return (event.returnValue =
              'Sie haben ungespeicherte Daten, wollen Sie die Seite wirklich verlassen?');
          }
        }),
        false
      );
      window.addEventListener('unload', this.onSocketDisconnect);
      window.addEventListener('popstate', this.onSocketDisconnect);

      await this.loadWorkflowData();

      this.setLoader(false);
      this.shouldRender = true;
      setTimeout(async () => {
        if (this.process) {
          this.connectSocket();
        }
      }, 0);
    } catch (error) {
      this.setLoader(false);
      this.shouldRender = true;
      if (error.status === 400 && error.data?.message === 'Access denied.') {
        this.showAccessDeniedToast();
      }
    }
  },
  beforeDestroy() {
    window.removeEventListener('beforeunload', this.unsavedDataHandler, false);
    window.removeEventListener('unload', this.onSocketDisconnect);
    window.addEventListener('popstate', this.onSocketDisconnect);
  },
  computed: {
    ...mapGetters({
      databaseList: 'getDatabaseList',
      isAdmin: 'getIsAdmin',
      user: 'getUser',
    }),
    isUserHasAccess() {
      return (
        this.flowPermission.access_level != workFlowAccessLevel.EDIT_CONTENT
      );
    },
    flowRunnerEndpoint() {
      if (
        this.process.settings.hasOwnProperty('apiEndpoint') &&
        this.process.settings.apiEndpoint != ''
      ) {
        return this.process.settings.apiEndpoint;
      } else return process.env.VUE_APP_FLOW_RUNNER_API;
    },
    flowWithoutInputOutput() {
      const flow = Object.assign({}, JSON.parse(JSON.stringify(this.process)));
      flow.tasks.forEach((part, index, arr) => {
        arr[index].output = [];
        arr[index].input = [];
      });
      return flow;
    },
    isTableIdArray() {
      if (typeof this.process.tableID === 'string') {
        return false;
      }

      if (Array.isArray(this.process.tableID)) {
        return true;
      }

      return null;
    },
    hasUnsavedChanges() {
      let flows = [
        JSON.parse(this.referenceProcess),
        JSON.parse(JSON.stringify(this.flowWithoutInputOutput)),
      ];

      // Remove irrelevant data for comparisation
      flows.forEach((f) => {
        f.tasks.forEach((t) => {
          delete t.shape.offsetx;
          delete t.shape.offsety;
          delete t.shape.selected;
        });
      });
      return JSON.stringify(flows[0]) != JSON.stringify(flows[1]);
    },
    getTasksArray() {
      return [...workFlowTasks, ...this.customTasks];
    },
    getTasksMiscData() {
      return { ...workFlowTasksMiscData, ...this.customMiscData };
    },
    getCustomTaskDatabase() {
      return this.databaseList.filter((ele) => ele.type == 'custom-task');
    },
    getWorkflowCustomTasks() {
      return workflowCustomTasksList;
    },
    isFlowEmpty() {
      return this.process.tasks.length === 0;
    },
    isFloatingTaskBar() {
      return this.taskbarMode === 'floating';
    },
  },
  watch: {
    async '$route.query'(newQueryParams, oldQueryParams) {
      if (newQueryParams['process'] !== oldQueryParams['process']) {
        try {
          this.onSocketDisconnect(oldQueryParams['process']);
          await this.loadWorkflowData();
          setTimeout(() => {
            if (this.process) {
              this.connectSocket();
              window.addEventListener('unload', this.onSocketDisconnect);
              window.addEventListener('popstate', this.onSocketDisconnect);
            }
          }, 0);
        } catch (error) {
          await this.$router.push({
            path: '/',
            query: {
              section: 'flows',
            },
          });

          if (
            error.status === 400 &&
            error.data?.message === 'Access denied.'
          ) {
            this.showAccessDeniedToast();
          }
        }
      }
    },
  },
  components: {
    WorkFlowHeader,
    WorkFlowTaskBar,
    WorkFlowBuilder,
    HtmlViewer,
  },
};
</script>
<style scoped>
*,
::before,
::after {
  box-sizing: border-box !important;
}

.workflow {
  color: #333343;
  background: #ffffff;
  /* height: 100%; */
  width: 100%;
  display: flex;
  flex-direction: column;
}
.workflow .workflow-body {
  display: flex;
  flex-grow: 1;
  justify-content: center;
  align-items: center;
  /* height: calc(100dvh - 88px); */
}

.workflow .workflow-loader {
  width: 100%;
  height: 100%;
  background: #fff;
  left: 0;
  top: 0;
  position: fixed;
  z-index: 1;
}

.workflow .workflow-loader .progress-icon {
  top: 50%;
  transform: translateY(-50%);
  height: fit-content;
  width: fit-content;
  left: 0;
  right: 0;
  margin: 0 auto;

  position: absolute;
}

.workflow .workflow-loader .progress-icon:after {
  content: ' ';
  display: block;
  width: 70px;
  height: 70px;
  margin: 1px;
  border-radius: 50%;
  border: 2px solid #000000;
  border-color: #000000 transparent #000000 transparent;
  animation: progress-icon 1.2s linear infinite;
}

.workflow .workflow-taskbar-wrapper {
  position: relative;
  height: 100%;
  width: 358px;
  flex-shrink: 0;
  transition: width 0.3s ease;
}

.workflow .empty-workflow-container {
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
}

.workflow .empty-workflow-container .init-task-add-button {
  text-align: center;
  font-weight: 100;
  cursor: pointer;
  display: -webkit-inline-box;
  display: -ms-inline-flexbox;
  display: inline-flex;
  -webkit-box-pack: center;
  -ms-flex-pack: center;
  justify-content: center;
  -webkit-box-align: center;
  -ms-flex-align: center;
  align-items: center;
  padding: 5px;
  border-radius: 50%;
  font-size: 40px;
  transition: 0.2s ease-in-out all;
  height: 58px;
  width: 58px;
  border: 0.5px solid #000;
  background-color: #000;
}

.workflow .empty-workflow-container .init-task-add-button svg path {
  stroke: #fff;
}
.workflow .empty-workflow-container .init-task-add-button:hover {
  color: #fff;
  border-color: #b3b3b3;
  background-color: transparent;
}

.workflow .empty-workflow-container .init-task-add-button:hover svg path {
  stroke: #b3b3b3;
}
</style>
